// Core API request function
import { shouldUseDirectNextProxy, ensureLeadingSlash } from './constants'
import { normalizeToError } from './helpers'
import { useAuthStore } from '@/stores/auth'
import type { ApiResponse } from '@/types'

// Generic API request function
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const normalizedEndpoint = ensureLeadingSlash(endpoint)
    const useDirectProxy = shouldUseDirectNextProxy(normalizedEndpoint)

    let url: string
    if (useDirectProxy) {
      // Route through the Next.js API routes that already encapsulate
      // additional response shaping and authorization behaviour.
      url = `/api${normalizedEndpoint}`
    } else {
      // Use the generalized proxy that injects the default site identifier on the server.
      url = `/api/be${normalizedEndpoint}`
    }

    // Get appropriate token from Zustand store based on endpoint type
    const isAgentEndpoint =
      normalizedEndpoint.startsWith('/agent/') ||
      normalizedEndpoint.startsWith('/sessions/') ||
      normalizedEndpoint.includes('/available-agents') ||
      normalizedEndpoint.includes('/transfer')
    const isUserEndpoint =
      normalizedEndpoint.startsWith('/users/') ||
      normalizedEndpoint.startsWith('/public/tickets/user/') ||
      normalizedEndpoint.startsWith('/public/tickets/get-token/') ||
      normalizedEndpoint.includes('/login') ||
      normalizedEndpoint.includes('/signup') ||
      normalizedEndpoint.includes('/verify') ||
      normalizedEndpoint.includes('/change-password') ||
      normalizedEndpoint.includes('/reset-password')
    const authState =
      typeof window !== 'undefined' ? useAuthStore.getState() : null
    // Prefer the token that matches the endpoint type, but gracefully fall
    // back to whichever credential is available so admin-only screens that
    // call agent/session endpoints still authenticate properly.
    const token = isUserEndpoint
      ? (authState?.userToken ??
        authState?.adminToken ??
        authState?.agentToken ??
        null)
      : isAgentEndpoint
        ? (authState?.agentToken ??
          authState?.adminToken ??
          authState?.userToken ??
          null)
        : (authState?.adminToken ??
          authState?.agentToken ??
          authState?.userToken ??
          null)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // For FormData, Content-Type should be set by browser, so remove it
    if (options.body instanceof FormData) {
      delete headers['Content-Type']
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    // Handle cases where response might not be JSON (e.g. 204 No Content for DELETE)
    if (response.status === 204) {
      return {
        status: response.status,
        success: true,
        data: undefined as unknown as T,
      } as ApiResponse<T>
    }

    // Read raw text first to robustly handle non-JSON or empty bodies
    let data: any
    try {
      data = await response.json()
    } catch (parseError) {
      // For JSON parsing errors, always throw regardless of status
      throw new Error('Invalid JSON response')
    }

    if (!response.ok) {
      const errorLogPayload =
        data && typeof data === 'object' && Object.keys(data).length > 0
          ? data
          : 'Response body could not be parsed'

      const messageFromPayload =
        (typeof data?.message === 'string' && data.message.trim().length > 0
          ? data.message.trim()
          : null) ??
        (typeof data?.error === 'string' && data.error.trim().length > 0
          ? data.error.trim()
          : null)

      const errorMessage =
        messageFromPayload || `HTTP error! status: ${response.status}`

      // For HTTP errors, return a structured response instead of throwing
      return {
        success: false,
        error: errorMessage,
        status: response.status,
        ...(data && typeof data === 'object' && 'message' in data
          ? { message: data.message }
          : {}),
      } as ApiResponse<T>
    }

    const extraFields: Partial<ApiResponse<T>> & { status: number } = {
      status: response.status,
    }

    if (data && typeof data === 'object') {
      if (typeof data.message === 'string') {
        extraFields.message = data.message
      }
      if ('pagination' in data && data.pagination) {
        extraFields.pagination = data.pagination
      }
      if ('meta' in data && data.meta) {
        extraFields.meta = data.meta
      }
      if ('user' in data && data.user) {
        extraFields.user = data.user
      }
      if ('token' in data && data.token) {
        extraFields.token = data.token
      }
    }

    if (typeof data?.success === 'boolean') {
      if (!data.success) {
        return {
          success: false,
          data: data?.data as T | undefined,
          error:
            typeof data?.error === 'string'
              ? data.error
              : data?.message || 'Request failed',
          ...extraFields,
        }
      }

      return {
        success: true,
        data:
          data && typeof data === 'object' && 'data' in data
            ? (data.data as T)
            : (data as T),
        ...extraFields,
      }
    }

    const hasError = typeof data?.error === 'string'
    const legacyStatus =
      typeof data?.status === 'string' ? data.status : undefined
    const success = legacyStatus ? legacyStatus !== 'error' : !hasError

    return {
      success,
      data: hasError ? undefined : (data as T),
      error: hasError ? data.error : undefined,
      ...extraFields,
    }
  } catch (error: any) {
    const normalizedError = normalizeToError(
      error,
      `API request to ${endpoint} failed`
    )
    // Suppress noisy logs for deliberate cancellations
    throw normalizedError
  }
}
